import React, { useEffect, useState } from 'react'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../../ui/dialog'
import { Button } from '../../ui/button'
import { Input } from '../../ui/input'
import { Label } from '../../ui/label'
import { Textarea } from '../../ui/textarea'
import { Badge } from '../../ui/badge'
import { AlertCircle, Check, Loader2, X } from 'lucide-react'
import ReportEndpoints from '../../../endpoints/ReportEndpoints'
import { SimpleIdentity } from '../../../model/Identity'
import { extractErrorMessage } from '../../../common/extractErrorMessage'
import { toast } from 'sonner'

// How many report+send round-trips to run at once. Each one renders a report
// and sends an SMTP message on the server, so this stays deliberately low.
const CONCURRENCY = 3

export interface BulkEmailTarget {
  identity: SimpleIdentity
  spotIds: number[]
}

interface SendResult {
  identity: SimpleIdentity
  spotIds: number[]
  error?: string
}

interface BulkEmployeeEmailDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  /** Employees with an email address — one personalised report each. */
  targets: BulkEmailTarget[]
  /** Employees that were selected but have no email address on file. */
  skipped: SimpleIdentity[]
  clientId: number | null
  defaultSubject?: string
  /**
   * Generates the report for one employee. Must throw on failure — the dialog
   * reports per-employee errors in its own summary rather than as toasts.
   */
  generateReport: (
    target: BulkEmailTarget
  ) => Promise<{ content: string; signature: string }>
}

const fullName = (i: SimpleIdentity) =>
  `${i.firstName || ''} ${i.lastName || ''}`.trim() || `Identity ${i.id}`

export const BulkEmployeeEmailDialog: React.FC<BulkEmployeeEmailDialogProps> = ({
  open,
  onOpenChange,
  targets,
  skipped,
  clientId,
  defaultSubject,
  generateReport,
}) => {
  const [subject, setSubject] = useState('')
  const [customMessage, setCustomMessage] = useState('')
  const [phase, setPhase] = useState<'compose' | 'sending' | 'done'>('compose')
  const [completed, setCompleted] = useState(0)
  // Size of the run in flight — the full list, or just the failures on a retry.
  const [batchSize, setBatchSize] = useState(0)
  const [sent, setSent] = useState<SendResult[]>([])
  const [failed, setFailed] = useState<SendResult[]>([])

  useEffect(() => {
    if (open) {
      setSubject(defaultSubject || 'Your Schedule')
      setCustomMessage('')
      setPhase('compose')
      setCompleted(0)
      setBatchSize(0)
      setSent([])
      setFailed([])
    }
  }, [open, defaultSubject])

  const send = async (batch: BulkEmailTarget[]) => {
    if (!subject.trim()) {
      toast.error('Subject is required')
      return
    }

    setPhase('sending')
    setCompleted(0)
    setBatchSize(batch.length)
    const succeeded: SendResult[] = []
    const errored: SendResult[] = []

    // Shared work queue — CONCURRENCY workers pull from it until it drains, so
    // a slow report never blocks the others behind it.
    const queue = [...batch]
    const worker = async () => {
      for (let target = queue.shift(); target; target = queue.shift()) {
        try {
          const report = await generateReport(target)
          await ReportEndpoints.sendReportEmail({
            subject,
            recipients: [target.identity.email],
            content: report.content,
            customMessage,
            signature: report.signature,
            clientId,
          })
          succeeded.push({ identity: target.identity, spotIds: target.spotIds })
        } catch (err) {
          errored.push({
            identity: target.identity,
            spotIds: target.spotIds,
            error: extractErrorMessage(err, 'Failed to send'),
          })
        } finally {
          setCompleted((c) => c + 1)
        }
      }
    }

    await Promise.all(
      Array.from({ length: Math.min(CONCURRENCY, batch.length) }, worker)
    )

    // Keep earlier successes when this run was a retry of the failures.
    setSent((prev) => [...prev, ...succeeded])
    setFailed(errored)
    setPhase('done')

    if (errored.length === 0) {
      toast.success(
        `Emailed ${succeeded.length} employee${succeeded.length === 1 ? '' : 's'}`
      )
    } else {
      toast.error(
        `${errored.length} of ${batch.length} email${batch.length === 1 ? '' : 's'} failed`
      )
    }
  }

  const total = targets.length
  const sending = phase === 'sending'

  return (
    <Dialog open={open} onOpenChange={(o) => !sending && onOpenChange(o)}>
      <DialogContent className="sm:max-w-[560px]">
        <DialogHeader>
          <DialogTitle>Email Each Employee Separately</DialogTitle>
          <DialogDescription>
            Every employee gets their own schedule — only their shifts, with
            their POS PIN on it.
          </DialogDescription>
        </DialogHeader>

        {phase === 'compose' && (
          <div className="space-y-4 py-2">
            <div className="rounded-md border p-3 space-y-2">
              <div className="flex items-center gap-2 text-sm">
                <Badge variant="secondary">{total}</Badge>
                <span>
                  employee{total === 1 ? '' : 's'} will receive a separate email
                </span>
              </div>
              {skipped.length > 0 && (
                <div className="flex items-start gap-2 text-sm text-muted-foreground">
                  <AlertCircle className="h-4 w-4 mt-0.5 shrink-0 text-amber-500" />
                  <span>
                    <span className="font-medium text-foreground">
                      {skipped.length} skipped
                    </span>{' '}
                    — no email address on file:{' '}
                    {skipped.map(fullName).join(', ')}
                  </span>
                </div>
              )}
              <div className="max-h-32 overflow-y-auto text-xs text-muted-foreground space-y-1 pt-1">
                {targets.map((t) => (
                  <div key={t.identity.id} className="flex justify-between gap-3">
                    <span className="truncate">
                      {fullName(t.identity)} — {t.identity.email}
                    </span>
                    <span className="shrink-0">
                      {t.spotIds.length} shift{t.spotIds.length === 1 ? '' : 's'}
                    </span>
                  </div>
                ))}
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="bulk-email-subject" className="text-sm font-medium">
                Subject
              </Label>
              <Input
                id="bulk-email-subject"
                type="text"
                value={subject}
                onChange={(e) => setSubject(e.target.value)}
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="bulk-email-message" className="text-sm font-medium">
                Message (Optional)
              </Label>
              <Textarea
                id="bulk-email-message"
                value={customMessage}
                onChange={(e) => setCustomMessage(e.target.value)}
                rows={4}
                placeholder="Sent to every recipient above the schedule."
              />
            </div>
          </div>
        )}

        {sending && (
          <div className="space-y-3 py-6">
            <div className="flex items-center justify-center gap-2 text-sm">
              <Loader2 className="h-4 w-4 animate-spin" />
              Sending {completed} of {batchSize}...
            </div>
            <div className="h-2 w-full rounded-full bg-muted overflow-hidden">
              <div
                className="h-full bg-primary transition-all"
                style={{
                  width: `${batchSize ? (completed / batchSize) * 100 : 0}%`,
                }}
              />
            </div>
            <p className="text-xs text-center text-muted-foreground">
              Leave this dialog open until the send finishes.
            </p>
          </div>
        )}

        {phase === 'done' && (
          <div className="space-y-3 py-2">
            <div className="flex items-center gap-2 text-sm">
              <Check className="h-4 w-4 text-green-600" />
              {sent.length} sent
              {failed.length > 0 && (
                <>
                  <X className="h-4 w-4 text-destructive ml-3" />
                  {failed.length} failed
                </>
              )}
            </div>
            {failed.length > 0 && (
              <div className="max-h-40 overflow-y-auto rounded-md border p-3 text-xs space-y-1">
                {failed.map((f) => (
                  <div key={f.identity.id}>
                    <span className="font-medium">{fullName(f.identity)}</span>
                    <span className="text-muted-foreground"> — {f.error}</span>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        <DialogFooter>
          {phase === 'compose' && (
            <>
              <Button variant="outline" onClick={() => onOpenChange(false)}>
                Cancel
              </Button>
              <Button onClick={() => send(targets)} disabled={total === 0}>
                Send {total} Email{total === 1 ? '' : 's'}
              </Button>
            </>
          )}
          {sending && (
            <Button disabled>
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              Sending...
            </Button>
          )}
          {phase === 'done' && (
            <>
              <Button variant="outline" onClick={() => onOpenChange(false)}>
                Close
              </Button>
              {failed.length > 0 && (
                <Button
                  onClick={() =>
                    send(
                      failed.map((f) => ({
                        identity: f.identity,
                        spotIds: f.spotIds,
                      }))
                    )
                  }
                >
                  Retry {failed.length} Failed
                </Button>
              )}
            </>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
